export const ROOT_FOLDER_ID = 'root';
export const STORAGE_KEY = 'textlab.workspace.v1';
export const SAVE_DEBOUNCE_MS = 400;
export const DEFAULT_SAVE_MODE = 'debounced';
export const DEFAULT_FOLDER_TAG_COLOR = '#8e8e93';
export const LEGACY_DEFAULT_FOLDER_TAG_COLOR = '#8e8e93';

export const DEFAULT_APPEARANCE = {
  backgroundColor: '#ffffff',
  fontSize: 18,
  fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  lineHeight: 1.7,
  textColor: '#1f2937',
};

export const TAG_COLORS = [
  { label: 'Gray', value: '#8e8e93' },
  { label: 'Red', value: '#ff3b30' },
  { label: 'Orange', value: '#ff9500' },
  { label: 'Yellow', value: '#ffcc00' },
  { label: 'Green', value: '#34c759' },
  { label: 'Blue', value: '#007aff' },
  { label: 'Purple', value: '#af52de' },
];

export const GRID_SIZE_OPTIONS = [
  { label: 'Very Small', value: 'verySmall', minWidth: 128 },
  { label: 'Small', value: 'small', minWidth: 158 },
  { label: 'Medium', value: 'medium', minWidth: 196 },
  { label: 'Large', value: 'large', minWidth: 240 },
];

export const SORT_OPTIONS = [
  { label: 'A-Z', value: 'nameAsc' },
  { label: 'Z-A', value: 'nameDesc' },
  { label: 'Updated newest', value: 'updatedDesc' },
  { label: 'Updated oldest', value: 'updatedAsc' },
  { label: 'Created newest', value: 'createdDesc' },
  { label: 'Created oldest', value: 'createdAsc' },
];

export const FONT_FAMILIES = [
  {
    label: 'System',
    value: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  { label: 'Serif', value: 'Georgia, "Times New Roman", serif' },
  { label: 'Mono', value: '"SFMono-Regular", Consolas, "Liberation Mono", monospace' },
  { label: 'Readable', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Draft', value: '"Courier New", Courier, monospace' },
];

export const FILE_COLOR_PRESETS = [
  { label: 'Paper', value: '#ffffff' },
  { label: 'Warm', value: '#fff7ed' },
  { label: 'Lemon', value: '#fef9c3' },
  { label: 'Mint', value: '#dcfce7' },
  { label: 'Sky', value: '#e0f2fe' },
  { label: 'Lavender', value: '#ede9fe' },
  { label: 'Rose', value: '#ffe4e6' },
  { label: 'Slate', value: '#1f2937' },
  { label: 'Ink', value: '#0f172a' },
];

const TEXT_FORMAT = {
  bold: 1,
  italic: 2,
  underline: 8,
};

function createTextNode(text, format = 0) {
  return {
    detail: 0,
    format,
    mode: 'normal',
    style: '',
    text,
    type: 'text',
    version: 1,
  };
}

function createElementNode(type, children, extra = {}) {
  return {
    children,
    direction: null,
    format: '',
    indent: 0,
    type,
    version: 1,
    ...extra,
  };
}

function createParagraph(children) {
  return createElementNode('paragraph', children);
}

function createListItem(value, children) {
  return createElementNode('listitem', children, { value });
}

function createEditorState(children) {
  return JSON.stringify({
    root: createElementNode('root', children),
  });
}

export const WELCOME_CONTENT = `Welcome to TextLab.

TextLab is a local-first text workspace. Create folders, add .txt files, pin important items, tag folders, and customize each file's writing surface.

Everything is stored in this browser with localStorage. There is no account and no backend.

Try this:
1. Create a folder from the explorer.
2. Add a text file.
3. Open it and use the settings drawer to adjust the page color, font, and spacing.
4. Use search to jump between folders and files.`;

export const WELCOME_EDITOR_STATE = createEditorState([
  createElementNode('heading', [createTextNode('Welcome to TextLab')], { tag: 'h1' }),
  createParagraph([
    createTextNode('TextLab is a '),
    createTextNode('local-first', TEXT_FORMAT.bold),
    createTextNode(' writing workspace for folders, files, favorites, and fast notes.'),
  ]),
  createElementNode('quote', [
    createTextNode('Everything is stored in this browser. No accounts, no backend, no sync setup.'),
  ]),
  createParagraph([
    createTextNode('Try selecting text and using the toolbar for '),
    createTextNode('bold', TEXT_FORMAT.bold),
    createTextNode(', '),
    createTextNode('italic', TEXT_FORMAT.italic),
    createTextNode(', and '),
    createTextNode('underline', TEXT_FORMAT.underline),
    createTextNode(' styles.'),
  ]),
  createElementNode(
    'list',
    [
      createListItem(1, [createTextNode('Create folders beside files like a real explorer.')]),
      createListItem(2, [createTextNode('Pin important items and filter them later.')]),
      createListItem(3, [createTextNode('Tune every file with its own page color, font, and spacing.')]),
    ],
    { listType: 'number', start: 1, tag: 'ol' }
  ),
  createElementNode(
    'list',
    [
      createListItem(1, [createTextNode('Use search to jump anywhere in the workspace.')]),
      createListItem(2, [createTextNode('Download files as plain .txt whenever you need them.')]),
    ],
    { listType: 'bullet', start: 1, tag: 'ul' }
  ),
]);

export const SCRATCHPAD_CONTENT = `Scratchpad

Use this file for quick drafts, outlines, and temporary notes.

Write freely, then move useful ideas into folders.`;

export const SCRATCHPAD_EDITOR_STATE = createEditorState([
  createElementNode('heading', [createTextNode('Scratchpad')], { tag: 'h1' }),
  createParagraph([
    createTextNode('Use this file for '),
    createTextNode('quick drafts', TEXT_FORMAT.bold),
    createTextNode(', outlines, and temporary notes.'),
  ]),
  createElementNode('quote', [createTextNode('A clean place for ideas before they become structure.')]),
  createElementNode(
    'list',
    [
      createListItem(1, [createTextNode('Capture the rough idea.')]),
      createListItem(2, [createTextNode('Refine it into clear notes.')]),
      createListItem(3, [createTextNode('Move it into the right folder.')]),
    ],
    { listType: 'number', start: 1, tag: 'ol' }
  ),
]);

export const STYLE_DEMO_CONTENT = `Style Demo

This warm note shows that every file can have its own writing surface.

Use it as a quick reference for formatting, colors, and lists.`;

export const STYLE_DEMO_EDITOR_STATE = createEditorState([
  createElementNode('heading', [createTextNode('Style Demo')], { tag: 'h1' }),
  createParagraph([
    createTextNode('This file uses the '),
    createTextNode('Warm', TEXT_FORMAT.bold),
    createTextNode(' card style, while new files still start with the Paper default.'),
  ]),
  createElementNode('quote', [
    createTextNode('Each file can keep its own color, typography, and editor feel.'),
  ]),
  createParagraph([
    createTextNode('Formatting can be mixed inline: '),
    createTextNode('bold', TEXT_FORMAT.bold),
    createTextNode(', '),
    createTextNode('italic', TEXT_FORMAT.italic),
    createTextNode(', and '),
    createTextNode('underlined', TEXT_FORMAT.underline),
    createTextNode(' text.'),
  ]),
  createElementNode(
    'list',
    [
      createListItem(1, [createTextNode('Open file settings to change this color.')]),
      createListItem(2, [createTextNode('Reset appearance to follow the workspace default again.')]),
      createListItem(3, [createTextNode('Download as .txt when you need a plain text copy.')]),
    ],
    { listType: 'bullet', start: 1, tag: 'ul' }
  ),
]);
